import uuid from '@vend/vend-uuid'
import { vn } from 'vend-number'
import { isDefinedFulfilmentType } from '../models/fulfilment'
import { LineItem } from '../models/line-item'
import { UnfulfilledSaleEditingSummaryLineItem } from '../queries/unfulfilled-sale-editing'

type UnfulfilledSaleEditingSummaryGroup = {
  lineItems: UnfulfilledSaleEditingSummaryLineItem[] | null
}

// Ensure the summary line items are flattened into a single array for easier processing
export const flattenUnfulfilledSaleEditingSummaries = (
  groups: UnfulfilledSaleEditingSummaryGroup[]
): UnfulfilledSaleEditingSummaryLineItem[] =>
  groups.flatMap(group => group.lineItems || [])

const isValidQuantity = (quantity: string | number | undefined) => {
  if (
    quantity === undefined ||
    quantity === '' ||
    (typeof quantity === 'string' &&
      !/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(quantity))
  ) {
    return false
  }

  return vn(quantity).isFinite()
}

// Virtually splits partially fulfilled line items into two separate line items,
// one for the fulfilled quantity and one for the unfulfilled quantity.
// The unfulfilled line item is marked as editable for continued sale.
export const applyUnfulfilledSaleEditability = (
  lineItems: LineItem[],
  summaries: UnfulfilledSaleEditingSummaryLineItem[]
): LineItem[] => {
  const summaryByLineId = new Map(
    summaries
      .filter(
        summary =>
          summary.saleLineItemID !== null && summary.fulfilledQuantity !== null
      )
      .map(summary => [summary.saleLineItemID!, summary])
  )

  return lineItems.flatMap(lineItem => {
    if (
      !lineItem.id ||
      !isDefinedFulfilmentType(lineItem.fulfilmentType) ||
      !isValidQuantity(lineItem.quantity)
    ) {
      return [lineItem]
    }

    const quantity = vn(lineItem.quantity)
    if (quantity.lessThanOrEqualTo(0)) {
      return [lineItem]
    }

    const summary = summaryByLineId.get(lineItem.id)
    if (
      !summary ||
      !isValidQuantity(summary.fulfilledQuantity!) ||
      (summary.unfulfilledQuantity !== null &&
        !isValidQuantity(summary.unfulfilledQuantity))
    ) {
      return [lineItem]
    }

    const fulfilledQuantity = vn(summary.fulfilledQuantity!)
    const remainingQuantity = quantity.sub(fulfilledQuantity)

    if (
      fulfilledQuantity.lessThan(0) ||
      (summary.unfulfilledQuantity !== null &&
        !remainingQuantity.eq(summary.unfulfilledQuantity))
    ) {
      return [lineItem]
    }

    if (fulfilledQuantity.isZero()) {
      return [{ ...lineItem, isEditableForContinue: true }]
    }

    if (fulfilledQuantity.greaterThanOrEqualTo(quantity)) {
      return [lineItem]
    }

    // A partially fulfilled line item is still on a picklist and so is never
    // marked completed by getItemFulfilmentMap. Fail closed if it is.
    if (lineItem.fulfilmentCompleted) {
      return [lineItem]
    }

    const fulfilled = fulfilledQuantity.toString()
    const remaining = remainingQuantity.toString()
    const editableLocalId = uuid()

    return [
      {
        ...lineItem,
        quantity: fulfilled,
        displayQuantity: fulfilled,
        fulfilmentCompleted: true,
        // Stamped on the locked half, which survives even when the editable
        // half is deleted.
        continuationEditableLocalId: editableLocalId,
      },
      {
        ...lineItem,
        id: undefined,
        localId: editableLocalId,
        quantity: remaining,
        displayQuantity: remaining,
        originalQuantity: remaining,
        isEditableForContinue: true,
        fulfilmentCompleted: false,
        // Records which line item this half was split off from, so fulfilments can pair the two
        // halves back up once the sale is saved. See splitFromLineItemId on LineItem.
        splitFromLineItemId: lineItem.id,
      },
    ]
  })
}
